import React, { useState, useEffect, useRef, useContext } from 'react';
import { IoSearch, IoLocation, IoClose } from 'react-icons/io5';
import { LocationContext } from '../../Contexts/LocationContext';
import instance from '../../instance/AxiosInstance';
import styles from './index.module.css';

// Generate unique session token for cost optimization
const generateSessionToken = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const LocationSearchInput = ({ setCoordinates, setAddressInfo, addressInfo }) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [placeholder, setPlaceholder] = useState('Search location...');
  
  const searchTimeout = useRef(null);
  const sessionToken = useRef(generateSessionToken()).current;
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const { location, setLocation } = useContext(LocationContext);

  // Update placeholder dynamically whenever location context changes
  useEffect(() => {
    console.log("📍 Location context updated, updating placeholder:", location);
    
    if (location?.city && location?.district) {
      const newPlaceholder = `${location.city}, ${location.district}`;
      setPlaceholder(newPlaceholder);
      console.log("✅ Placeholder updated to:", newPlaceholder);
    } else if (location?.district && location?.state) {
      const newPlaceholder = `${location.district}, ${location.state}`;
      setPlaceholder(newPlaceholder);
      console.log("✅ Placeholder updated to:", newPlaceholder);
    } else if (location?.district) {
      setPlaceholder(location.district);
      console.log("✅ Placeholder updated to:", location.district);
    } else if (location?.city) {
      setPlaceholder(location.city);
      console.log("✅ Placeholder updated to:", location.city);
    } else {
      setPlaceholder('Search location...');
      console.log("⚠️ No location data, using default placeholder");
    }
  }, [location, location?.city, location?.district, location?.state, location?.country]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search - triggers 500ms after user stops typing
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (searchText.trim().length > 2) {
      setIsSearching(true);
      searchTimeout.current = setTimeout(() => {
        handleGooglePlacesSearch(searchText.trim());
      }, 500);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchText]);

  // Google Places Autocomplete API via Backend
  const handleGooglePlacesSearch = async (query) => {
    setIsSearching(true);
    try {
      const response = await instance.post('/api/location/autocomplete', {
        query,
        sessionToken,
      });

      const data = response.data;

      if (data.success && data.predictions) {
        const formattedResults = data.predictions.map((prediction) => ({
          placeId: prediction.place_id,
          description: prediction.description,
          mainText: prediction.structured_formatting?.main_text || prediction.description,
          secondaryText: prediction.structured_formatting?.secondary_text || '',
        }));
        setSearchResults(formattedResults);
        setShowDropdown(true);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('❌ Error fetching autocomplete:', error);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Fetch place details using Place ID
  const fetchPlaceDetails = async (placeId) => {
    try {
      console.log("🔍 Fetching place details for Place ID:", placeId);
      
      const response = await instance.post('/api/location/place-details', {
        placeId,
        sessionToken,
      });

      const data = response.data;

      if (data.success && data.result) {
        const result = data.result;
        
        console.log("✅ Place details fetched:", result);

        const locationData = {
          latitude: result.lat,
          longitude: result.lon,
          city: result.city,
          district: result.district,
          state: result.state,
          country: result.country,
          fullAddress: result.fullAddress,
          pincode: result.pincode,
          loading: false,
          error: null,
        };

        return locationData;
      } else {
        throw new Error(data.message || 'Failed to fetch place details');
      }
    } catch (error) {
      console.error('❌ Error fetching place details:', error);
      return null;
    }
  };

  const handleLocationSelect = async (item) => {
    console.log("📍 Location selected:", item.description);
    
    const locationData = await fetchPlaceDetails(item.placeId);

    if (locationData) {
      console.log("🔄 Updating location context with:", locationData);
      
      // Update context - this will trigger the placeholder update via useEffect
      setLocation((prev) => ({
        ...prev,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        city: locationData.city,
        district: locationData.district,
        state: locationData.state,
        country: locationData.country,
        loading: false,
        error: null,
      }));

      console.log("✅ Location context updated successfully");

      // Update coordinates callback
      if (setCoordinates) {
        setCoordinates({
          lat: locationData.latitude,
          lng: locationData.longitude,
        });
      }

      // Update address info callback
      if (setAddressInfo) {
        setAddressInfo({
          fullAddress: locationData.fullAddress,
          city: locationData.city,
          district: locationData.district,
          state: locationData.state,
          country: locationData.country,
          pincode: locationData.pincode,
        });
      }

      // Close dropdown and clear search
      setShowDropdown(false);
      setSearchText('');
      setSearchResults([]);
    } else {
      console.error("❌ Failed to get location data");
    }
  };

  const handleClearInput = () => {
    setSearchText('');
    setSearchResults([]);
    setShowDropdown(false);
  };

  return (
    <div className={styles.locationSearchContainer}>
      <div className={styles.inputWrapper} ref={inputRef}>
        <IoLocation className={styles.locationIcon} />
        <input
          type="text"
          className={styles.searchInput}
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
        />
        {searchText.length > 0 && (
          <button className={styles.clearButton} onClick={handleClearInput}>
            <IoClose />
          </button>
        )}
      </div>

      {/* Dropdown Suggestions */}
      {showDropdown && (searchResults.length > 0 || isSearching) && (
        <div className={styles.dropdownContainer} ref={dropdownRef}>
          {isSearching ? (
            <div className={styles.loadingState}>
              {[1, 2, 3].map((item) => (
                <div key={item} className={styles.skeletonItem}>
                  <div className={styles.skeletonIcon} />
                  <div className={styles.skeletonText}>
                    <div className={styles.skeletonLine1} />
                    <div className={styles.skeletonLine2} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className={styles.suggestionList}>
              {searchResults.map((item) => (
                <li
                  key={item.placeId}
                  className={styles.suggestionItem}
                  onClick={() => handleLocationSelect(item)}
                >
                  <IoLocation className={styles.suggestionIcon} />
                  <div className={styles.suggestionText}>
                    <p className={styles.mainText}>{item.mainText}</p>
                    <p className={styles.secondaryText}>{item.secondaryText}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSearchInput;